/**
 * PromptEditor — /crm/settings/prompts/:key. Full-page admin editor for one AI prompt.
 *
 * The working copy is a draft (PATCH /prompts/:key); Publish snapshots it into
 * an immutable version (POST /prompts/:key/publish) that generation starts using.
 * The right rail shows the version history with a "live" pill; any past version
 * can be viewed or restored into the draft. "Test" runs the (unsaved) draft
 * against a real study so you can see the output before publishing.
 *
 * Modeled on FormBuilder — same primary chrome, inline notice/error banners, and
 * draft→publish lifecycle.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, FlaskConical, Loader2, RotateCcw, Save, Sparkles, UploadCloud } from 'lucide-react';
import api from '../../../services/api';
import { relTime, useIsCRMAdmin } from '../../lib';
import { studyInternalLabel } from '../../types';
import Modal from '../../components/forms/Modal';
import VariablePicker from '../../components/VariablePicker';
import { Alert } from '../../components/ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

interface AllowedVar { name: string; description: string; }
interface PromptDetail {
  key: string;
  name: string;
  description: string;
  kind: 'system' | 'user';
  output_format: string;
  allowed_variables: AllowedVar[];
  default_template: string;
  default_max_tokens: number | null;
  draft_template: string;
  draft_max_tokens: number | null;
  version: number;
  status: string;
  updated_at: string | null;
  exists: boolean;
}
interface VersionSummary {
  id: string;
  version: number;
  change_note: string | null;
  published_by: string | null;
  published_at: string;
}
interface VersionFull extends VersionSummary { template: string; max_tokens: number | null; }
interface StudyChip { id: string | null; slug: string; title: string | null; internal_title?: string | null; source: string; }
interface TestResult {
  output: string;
  stopReason: string;
  truncated: boolean;
  inputTokens: number;
  outputTokens: number;
  modelId: string;
  artifactType: string;
  maxTokens: number;
}

const ARTIFACT_LABELS: Record<string, string> = {
  summary: 'Study summary',
  recruiter_script: 'Recruiter script',
  stipend_info: 'Stipend extraction',
  prescreen_draft: 'Prescreen questionnaire',
};

const inputCls =
  'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500';
const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300';

export default function PromptEditor() {
  const { key = '' } = useParams<{ key: string }>();
  const isAdmin = useIsCRMAdmin();

  const [prompt, setPrompt] = useState<PromptDetail | null>(null);
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [template, setTemplate] = useState('');
  const [maxTokens, setMaxTokens] = useState<string>('');
  const [changeNote, setChangeNote] = useState('');
  const [dirty, setDirty] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Test panel
  const [studies, setStudies] = useState<StudyChip[]>([]);
  const [testStudyId, setTestStudyId] = useState('');
  const [testArtifact, setTestArtifact] = useState('summary');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  // Version view modal
  const [viewVersion, setViewVersion] = useState<VersionFull | null>(null);
  const [viewDefault, setViewDefault] = useState(false);

  const taRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/crm/prompts/${key}`);
      const p: PromptDetail = res.data.prompt;
      setPrompt(p);
      setVersions(res.data.versions || []);
      setTemplate(p.draft_template);
      setMaxTokens(p.draft_max_tokens != null ? String(p.draft_max_tokens) : '');
      setDirty(false);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => { load(); }, [load]);

  // Studies for the test picker (only adopted CRM studies can have documents).
  useEffect(() => {
    let alive = true;
    api.get('/api/crm/studies')
      .then((res) => {
        if (!alive) return;
        const list: StudyChip[] = (res.data?.studies || []).filter((s: StudyChip) => s.id && s.source === 'crm');
        setStudies(list);
        if (list.length && !testStudyId) setTestStudyId(list[0].id as string);
      })
      .catch(() => { /* test picker just stays empty */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const touch = (next: string) => { setTemplate(next); setDirty(true); };

  const insertVar = (token: string) => {
    const ta = taRef.current;
    if (!ta) { touch(template + token); return; }
    const start = ta.selectionStart ?? template.length;
    const end = ta.selectionEnd ?? template.length;
    touch(template.slice(0, start) + token + template.slice(end));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const maxTokensNum = (): number | null => {
    const t = maxTokens.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? Math.round(n) : null;
  };

  const save = async () => {
    if (saving || !template.trim()) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.patch(`/api/crm/prompts/${key}`, { template, max_tokens: maxTokensNum() });
      setDirty(false);
      setNotice('Draft saved. Publish to make it live.');
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (publishing || !template.trim()) return;
    setPublishing(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.post(`/api/crm/prompts/${key}/publish`, {
        template,
        max_tokens: maxTokensNum(),
        change_note: changeNote.trim() || undefined,
      });
      setChangeNote('');
      setNotice(`Published v${res.data?.version?.version} — now live for all future generations.`);
      await load();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setPublishing(false);
    }
  };

  const restore = async (version: number) => {
    setError(null);
    setNotice(null);
    try {
      await api.post(`/api/crm/prompts/${key}/revert`, { version });
      setNotice(`Restored v${version} into the draft. Review it, then Publish to make it live.`);
      setViewVersion(null);
      await load();
    } catch (e: any) {
      setError(errMsg(e));
    }
  };

  const openVersion = async (version: number) => {
    setError(null);
    try {
      const res = await api.get(`/api/crm/prompts/${key}/versions/${version}`);
      setViewVersion(res.data.version);
    } catch (e: any) {
      setError(errMsg(e));
    }
  };

  const resetToDefault = () => {
    if (!prompt) return;
    touch(prompt.default_template);
    setMaxTokens(prompt.default_max_tokens != null ? String(prompt.default_max_tokens) : '');
    setNotice('Loaded the built-in default into the draft. Publish to make it live.');
  };

  const runTest = async () => {
    if (testing || !testStudyId || !template.trim()) return;
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const res = await api.post(`/api/crm/prompts/${key}/test`, {
        template,
        max_tokens: maxTokensNum(),
        study_id: testStudyId,
        artifact_type: prompt?.kind === 'system' ? testArtifact : undefined,
      });
      setTestResult(res.data.result);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <Link to="/crm/settings/prompts" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400">
          <ArrowLeft className="h-4 w-4" /> AI Prompts
        </Link>
        <Alert tone="error" icon={false} className="mt-4">{error || 'Prompt not found.'}</Alert>
      </div>
    );
  }

  const liveVersion = prompt.version;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <Link to="/crm/settings/prompts" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400">
        <ArrowLeft className="h-4 w-4" /> AI Prompts
      </Link>

      {/* Header card */}
      <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="inline-flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
              <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              {prompt.name}
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">{prompt.description}</p>
            <p className="mt-1.5 text-xs text-gray-400">
              {liveVersion > 0 ? `Live: v${liveVersion}` : 'Live: built-in default'}
              {' · '}{prompt.kind === 'system' ? 'system prompt' : 'user prompt'}
              {prompt.output_format && prompt.output_format !== 'none' ? ` · outputs ${prompt.output_format.replace('_', ' ')}` : ''}
            </p>
          </div>
          {isAdmin && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={save}
                disabled={saving || !dirty || !template.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save draft
              </button>
              <button
                onClick={publish}
                disabled={publishing || !template.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                Publish
              </button>
            </div>
          )}
        </div>

        {notice && (
          <Alert tone="info" size="sm" icon={false} className="mt-3">{notice}</Alert>
        )}
        {error && (
          <Alert tone="error" size="sm" icon={false} className="mt-3">{error}</Alert>
        )}
        {!isAdmin && (
          <Alert tone="warning" size="sm" icon={false} className="mt-3">
            You are viewing this prompt read-only. Editing is limited to CRM admins.
          </Alert>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Editor + test (main column) */}
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <label className={labelCls}>Prompt template</label>
            <textarea
              ref={taRef}
              value={template}
              onChange={(e) => touch(e.target.value)}
              disabled={!isAdmin}
              rows={20}
              spellCheck={false}
              className={`${inputCls} font-mono text-xs leading-relaxed`}
            />

            {/* Variables helper */}
            {prompt.allowed_variables.length > 0 && (
              <div className="mt-3">
                <p className="mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                  Filled in automatically at generation time{isAdmin ? ' — click to insert' : ''}:
                </p>
                <VariablePicker
                  items={prompt.allowed_variables.map((v) => ({ token: v.name, label: v.name, description: v.description }))}
                  onInsert={insertVar}
                  disabled={!isAdmin}
                />
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div>
                <label className={labelCls}>Max output tokens</label>
                <input
                  type="number"
                  min={256}
                  max={64000}
                  value={maxTokens}
                  onChange={(e) => { setMaxTokens(e.target.value); setDirty(true); }}
                  disabled={!isAdmin}
                  placeholder={prompt.default_max_tokens != null ? String(prompt.default_max_tokens) : 'default'}
                  className={`${inputCls} w-40`}
                />
              </div>
              {isAdmin && (
                <button
                  type="button"
                  onClick={resetToDefault}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <RotateCcw className="h-4 w-4" /> Reset to built-in default
                </button>
              )}
            </div>

            {isAdmin && (
              <div className="mt-4">
                <label className={labelCls}>Change note (optional — shown in the version history)</label>
                <input
                  type="text"
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                  placeholder="e.g. Softened the opening line; added a no-promises reminder"
                  className={inputCls}
                />
              </div>
            )}
          </div>

          {/* Test panel */}
          {isAdmin && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
              <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <FlaskConical className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                Test this prompt
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Runs the edit above (unsaved is fine) against a real study's document so you can see the
                output before publishing. The prescreen prompt can take up to a minute.
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div className="min-w-[200px] flex-1">
                  <label className={labelCls}>Study</label>
                  <select value={testStudyId} onChange={(e) => setTestStudyId(e.target.value)} className={inputCls}>
                    {studies.length === 0 && <option value="">No studies with documents yet</option>}
                    {studies.map((s) => (
                      <option key={s.id as string} value={s.id as string}>{studyInternalLabel(s)}</option>
                    ))}
                  </select>
                </div>
                {prompt.kind === 'system' && (
                  <div className="min-w-[200px]">
                    <label className={labelCls}>Pair with</label>
                    <select value={testArtifact} onChange={(e) => setTestArtifact(e.target.value)} className={inputCls}>
                      {Object.entries(ARTIFACT_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </div>
                )}
                <button
                  onClick={runTest}
                  disabled={testing || !testStudyId || !template.trim()}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
                  Run test
                </button>
              </div>

              {testResult && (
                <div className="mt-4">
                  <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
                    <span>{testResult.inputTokens.toLocaleString()} in · {testResult.outputTokens.toLocaleString()} out</span>
                    <span>· {testResult.modelId}</span>
                    {testResult.truncated && (
                      <span className="rounded bg-red-100 px-1.5 py-0.5 font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        cut off at {testResult.maxTokens.toLocaleString()} tokens — raise max tokens
                      </span>
                    )}
                  </div>
                  <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200">
                    {testResult.output}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Version history (right rail) */}
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Version history</h3>
            <p className="mt-1 text-xs text-gray-400">
              Each publish is saved permanently. Generation uses the live version; restoring loads an
              old version back into the draft.
            </p>
            <button
              type="button"
              onClick={() => setViewDefault(true)}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline dark:text-primary-400"
            >
              <Eye className="h-3.5 w-3.5" /> View built-in default
            </button>

            <div className="mt-3 space-y-2">
              {versions.length === 0 && (
                <p className="text-xs text-gray-400">No published versions yet — still on the built-in default.</p>
              )}
              {versions.map((v) => (
                <div key={v.id} className="rounded-lg border border-gray-100 p-2.5 dark:border-gray-700">
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                      v{v.version}
                      {v.version === liveVersion && (
                        <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300">
                          live
                        </span>
                      )}
                    </span>
                    <span className="text-[11px] text-gray-400">{relTime(v.published_at)}</span>
                  </div>
                  {v.change_note && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{v.change_note}</p>}
                  <div className="mt-1.5 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => openVersion(v.version)}
                      className="inline-flex min-h-[44px] items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 sm:min-h-0"
                    >
                      <Eye className="h-3.5 w-3.5" /> View
                    </button>
                    {isAdmin && v.version !== liveVersion && (
                      <button
                        type="button"
                        onClick={() => restore(v.version)}
                        className="inline-flex min-h-[44px] items-center gap-1 text-[11px] font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400 sm:min-h-0"
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Restore
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* View a past version */}
      <Modal
        isOpen={!!viewVersion}
        onClose={() => setViewVersion(null)}
        title={viewVersion ? `Version ${viewVersion.version}` : ''}
        maxWidth="max-w-3xl"
      >
        {viewVersion && (
          <div>
            <div className="mb-2 flex items-center justify-between gap-2 text-xs text-gray-400">
              <span>Published {relTime(viewVersion.published_at)}{viewVersion.max_tokens ? ` · ${viewVersion.max_tokens.toLocaleString()} max tokens` : ''}</span>
              {isAdmin && viewVersion.version !== liveVersion && (
                <button
                  type="button"
                  onClick={() => restore(viewVersion.version)}
                  className="inline-flex items-center gap-1 rounded-lg border border-primary-600 px-2.5 py-1 font-medium text-primary-700 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Restore into draft
                </button>
              )}
            </div>
            {viewVersion.change_note && <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">{viewVersion.change_note}</p>}
            <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200">
              {viewVersion.template}
            </pre>
          </div>
        )}
      </Modal>

      {/* View the built-in default */}
      <Modal isOpen={viewDefault} onClose={() => setViewDefault(false)} title="Built-in default" maxWidth="max-w-3xl">
        <div>
          <div className="mb-2 flex items-center justify-between gap-2 text-xs text-gray-400">
            <span>The original prompt shipped in code — the fallback when nothing is published.</span>
            {isAdmin && (
              <button
                type="button"
                onClick={() => { resetToDefault(); setViewDefault(false); }}
                className="inline-flex items-center gap-1 rounded-lg border border-primary-600 px-2.5 py-1 font-medium text-primary-700 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Load into draft
              </button>
            )}
          </div>
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200">
            {prompt.default_template}
          </pre>
        </div>
      </Modal>
    </div>
  );
}
